import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';

import ConfirmableButton from '../../../components/ConfirmableButton';
import { useToast } from '../../../context/ToastContext';
import { useUser } from '../../../hooks/useUser';
import { ToolName } from '../apis/types';
import { useBookingApiKeys } from '../hooks/useBookingApiKeys';
import {
  bookingSystemToToolName,
  getBookingSystemDisplayName,
} from '../utils/bookingSystemUtils';

const BookingApiKeys: React.FC = () => {
  const { apiKeys, loading, adding, deleting, error, addApiKey, deleteApiKey } =
    useBookingApiKeys();
  const { user, loading: userLoading } = useUser();
  const { showToast } = useToast();

  // Show error from React Query as toast
  useEffect(() => {
    if (error) {
      showToast('Failed to load API keys', error, 'error');
    }
  }, [error, showToast]);

  const [newApiKey, setNewApiKey] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<
    string | null
  >(null);

  const bookingSystem = user?.bookingSystem ?? 'datev';
  const requiredToolName = bookingSystemToToolName(bookingSystem);
  const systemDisplayName = getBookingSystemDisplayName(bookingSystem);

  const existingKey = apiKeys.find(k => k.toolName === requiredToolName);

  const getToolDisplayName = (toolName: ToolName): string => {
    const displayNames: Record<ToolName, string> = {
      lex_office: 'LexOffice',
      klardaten: 'Klardaten',
    };
    return displayNames[toolName] || toolName;
  };

  const handleAddApiKey = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // If editing, check if the key value actually changed
      if (isEditing && existingKey && existingKey.apiKey === newApiKey) {
        showToast(
          'No changes detected',
          'The API key value is the same as before.',
          'info'
        );
        setNewApiKey('');
        setShowAddForm(false);
        setIsEditing(false);
        return;
      }

      await addApiKey({ toolName: requiredToolName, apiKey: newApiKey });
      showToast(
        existingKey
          ? `${getToolDisplayName(requiredToolName)} API key updated`
          : `${getToolDisplayName(requiredToolName)} API key added`,
        'The API key has been saved successfully.',
        'success'
      );
      setNewApiKey('');
      setShowAddForm(false);
      setIsEditing(false);
    } catch (err) {
      showToast(
        'Failed to save API key',
        err instanceof Error ? err.message : 'An unexpected error occurred',
        'error'
      );
    }
  };

  const handleDeleteApiKey = async (toolName: ToolName) => {
    try {
      await deleteApiKey(toolName);
      showToast(
        `${getToolDisplayName(toolName)} API key deleted`,
        'The API key has been removed successfully.',
        'success'
      );
    } catch (err) {
      showToast(
        'Failed to delete API key',
        err instanceof Error ? err.message : 'An unexpected error occurred',
        'error'
      );
    }
  };

  const handleEditApiKey = (apiKey: string) => {
    setNewApiKey(apiKey);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setNewApiKey('');
    setShowAddForm(false);
    setIsEditing(false);
  };

  if (loading || userLoading) {
    return (
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
        <div className='animate-pulse'>
          <div className='h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4'></div>
          <div className='h-20 bg-gray-200 dark:bg-gray-700 rounded mb-2'></div>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
      <div className='flex justify-between items-center mb-4'>
        <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
          {systemDisplayName} API Key
        </h2>
        {!isEditing && !existingKey && (
          <button
            onClick={() => {
              if (showAddForm) {
                setShowAddForm(false);
                setNewApiKey('');
              } else {
                setShowAddForm(true);
              }
            }}
            className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium'
          >
            {showAddForm ? 'Cancel' : 'Add API Key'}
          </button>
        )}
      </div>

      {/* Warning Message for Missing API Key */}
      {!existingKey && !showAddForm && (
        <div className='bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6'>
          <div className='flex items-start gap-3'>
            <svg
              className='w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5'
              fill='currentColor'
              viewBox='0 0 20 20'
            >
              <path
                fillRule='evenodd'
                d='M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z'
                clipRule='evenodd'
              />
            </svg>
            <div className='flex-1'>
              <h3 className='text-sm font-medium text-amber-800 dark:text-amber-200 mb-1'>
                Add an API key to enable direct import
              </h3>
              <p className='text-sm text-amber-700 dark:text-amber-300'>
                Adding a {getToolDisplayName(requiredToolName)} API key will
                enable you to import bookings directly to your accounting
                system, streamlining your workflow.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit API Key Form */}
      {(showAddForm || isEditing) && (
        <form
          onSubmit={handleAddApiKey}
          className='mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg'
        >
          <div className='space-y-4'>
            <div>
              <span className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'>
                Tool
              </span>
              <div className='px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white'>
                {getToolDisplayName(requiredToolName)}
              </div>
            </div>
            <div>
              <label
                htmlFor='api-key-input'
                className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
              >
                API Key
              </label>
              <input
                id='api-key-input'
                type='text'
                value={newApiKey}
                onChange={e => setNewApiKey(e.target.value)}
                placeholder='Enter your API key'
                required
                className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              />
            </div>
            <div className='flex justify-end gap-2'>
              <button
                type='button'
                onClick={handleCancelEdit}
                disabled={adding}
                className='px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
              >
                Cancel
              </button>
              <button
                type='submit'
                disabled={adding || !newApiKey}
                className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
              >
                {adding
                  ? 'Saving...'
                  : existingKey
                    ? 'Save Changes'
                    : 'Save API Key'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* API Key Display */}
      {!isEditing && existingKey && (
        <div className='space-y-3'>
          <div
            className='flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700'
          >
            <div className='flex-1'>
              <div className='flex items-center space-x-3'>
                <span className='px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-sm font-medium rounded-full'>
                  {getToolDisplayName(existingKey.toolName)}
                </span>
                <span className='text-gray-600 dark:text-gray-400 font-mono text-sm break-all'>
                  {existingKey.apiKey}
                </span>
              </div>
              <p className='text-xs text-gray-500 dark:text-gray-400 mt-2'>
                Added: {new Date(existingKey.createdAt).toLocaleDateString()} at{' '}
                {new Date(existingKey.createdAt).toLocaleTimeString()}
              </p>
            </div>
            <div className='flex gap-2 items-center ml-4'>
              <button
                onClick={() => handleEditApiKey(existingKey.apiKey)}
                className='p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-md transition-colors'
                aria-label='Edit API key'
                style={{
                  visibility:
                    showDeleteConfirmation === existingKey._id
                      ? 'hidden'
                      : 'visible',
                }}
              >
                <PencilIcon className='w-5 h-5' />
              </button>
              <ConfirmableButton
                onConfirm={() => handleDeleteApiKey(existingKey.toolName)}
                onShowConfirmation={show =>
                  setShowDeleteConfirmation(show ? existingKey._id : null)
                }
                buttonClassName='p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 rounded-md transition-colors'
                confirmButtonClassName='p-1 bg-white dark:bg-gray-800 rounded-full border border-gray-300 dark:border-gray-600 text-green-600 hover:text-green-800 hover:border-green-600'
                cancelButtonClassName='p-1 bg-white dark:bg-gray-800 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 hover:text-gray-800 hover:border-gray-600'
              >
                {deleting ? (
                  <span className='text-sm px-1'>Deleting...</span>
                ) : (
                  <TrashIcon className='w-5 h-5' />
                )}
              </ConfirmableButton>
            </div>
          </div>
        </div>
      )}

      {/* No key state (when form is not shown) */}
      {!isEditing && !existingKey && !showAddForm && (
        <div className='text-center py-8 text-gray-500 dark:text-gray-400'>
          <p>No API key configured yet.</p>
        </div>
      )}
    </div>
  );
};

export default BookingApiKeys;
